import colors from '@Common/assets/colors';
import ConfirmationModal from '@Common/ConfirmationModal';
import ReactiveIcon from '@Common/ReactiveIcon';
import { parseDate } from '@Generator/history/GenColumns';
import { Trash, CaretDown, CaretRight } from '@phosphor-icons/react';
import LibraryItem from './LibraryItem';
import { LibraryPrompt } from '@Models/types';
import { useState } from 'react';
import { usePromptToolboxContext } from '@Context/prompt-toolbox/PromptToolboxContext';
import SuccessModal from '@Common/SuccessModal';
import { deleteLibraryPrompt } from '@Lib/prompt-toolbox';

interface LibraryTileProps {
  prompt: LibraryPrompt;
  index: number;
  selectedLibrary: LibraryPrompt[];
  onSelect: () => void;
  isAdmin: boolean;
}

const LibraryTile = (props: LibraryTileProps) => {
  const { prompt, index, selectedLibrary, onSelect, isAdmin } = props;
  const { fetchLibraryPrompts } = usePromptToolboxContext();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeDelete, setActiveDelete] = useState<number | null>(null);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const togglePromptItem = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const handleOpenDelete = (index: number | null) => () => {
    setActiveDelete(index);
    setOpenDelete((prev) => !prev);
  };

  const handleDelete = (promptId: string | undefined) => async () => {
    setLoading(true);
    if (!promptId) return;
    deleteLibraryPrompt(promptId)
      .then(() => {
        if (error) {
          setError(false);
        }
        setOpenDelete(false);
        setActiveDelete(null);
        setSuccess(true);
        setLoading(false);
      })
      .catch((error) => {
        setError(true);
        setOpenDelete(true);
        if (error instanceof Error) {
          console.log(`Error has Occured: ${error} `);
        } else {
          console.log(error);
        }
      });
  };
  const handleSuccessConfirm = async () => {
    try {
      setSuccess(false);
      await fetchLibraryPrompts();
    } catch (error) {
      if (error instanceof Error) {
        console.log(`Error has Occured: ${error} `);
      } else {
        console.log(error);
      }
    }
  };

  return (
    <>
      <div
        id={`library-tile-${prompt._id}`}
        key={`library-tile-key-${prompt._id}`}
      >
        <div
          className={`flex w-full min-w-[40rem] items-center justify-around text-sm md:text-base ${
            activeIndex === index
              ? 'rounded-t-lg bg-primary-700 text-white'
              : 'rounded-lg hover:bg-gray-300'
          } px-4 py-2 focus:outline-none`}
        >
          <div className="flex min-w-0 flex-[1] flex-row items-center gap-x-2">
            <input
              data-testid={`library-${prompt.name.replace(/\s+/g, '-')}`}
              checked={selectedLibrary.some((s) => s._id === prompt._id)}
              type="checkbox"
              onChange={(e) => {
                e.stopPropagation();
                onSelect();
              }}
              className="flex-shrink-0"
            />
            <span
              className="min-w-0 flex-1 truncate font-medium"
              title={prompt.name}
            >
              {prompt.name}
            </span>
          </div>
          <div className="flex flex-[2] flex-row items-center gap-x-2">
            <span className="flex-[2] truncate">{prompt.author}</span>
            <span className="flex-[2] whitespace-nowrap text-center">
              {parseDate(Number(prompt.version.timestamp))}
            </span>
            <div className="flex flex-shrink-0 flex-row items-center gap-x-2">
              {isAdmin && (
                <button
                  data-testid="test-lib-prompt-delete-btn"
                  data-tooltip-id="prev"
                  data-tooltip-content="Delete"
                  data-tooltip-place="left"
                  onClick={handleOpenDelete(index)}
                  className="flex-shrink-0"
                >
                  <ReactiveIcon
                    Icon={Trash}
                    color={
                      activeIndex === index ? 'white' : colors.brandRed[500]
                    }
                    clicked={activeDelete === index}
                  />
                </button>
              )}
              <button
                onClick={() => {
                  togglePromptItem(index);
                }}
                className="flex-shrink-0"
              >
                <span
                  data-testid="test-lib-expand-btn"
                  className="transition-transform"
                >
                  {activeIndex === index ? (
                    <CaretDown weight="bold" size={25} />
                  ) : (
                    <CaretRight weight="bold" size={25} />
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
        {activeIndex === index && (
          <LibraryItem key={`library-item-${prompt._id}`} id={prompt._id} />
        )}
      </div>
      <ConfirmationModal
        key={`confirmation-modal-${prompt._id}`}
        title={'Delete Prompt'}
        isOpen={openDelete}
        loading={loading}
        confirmButtonStyle={{
          label: error ? 'Retry' : 'Delete',
          color: 'RED',
        }}
        message={
          error ? (
            <span>Delete Failed!!!</span>
          ) : (
            <span>
              Do you wish to <b className="text-brandRed-300">DELETE </b>
              {prompt.name}?
            </span>
          )
        }
        onConfirm={handleDelete(prompt._id)}
        onCancel={handleOpenDelete(null)}
      />
      <SuccessModal
        key={`success-modal-${prompt._id}`}
        title={'Success'}
        message={`Delete Successful!!!`}
        isOpen={success}
        confirmButtonStyle={{ label: 'OK' }}
        onConfirm={handleSuccessConfirm}
      />
    </>
  );
};

export default LibraryTile;
